import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import type { Point2D } from '@shared/models/geometry.model';
import type { Storey } from '@shared/models/design/storey.model';
import { rotatePoint2DClockwise } from './blueprint-underlay-geometry.helpers';
import {
  getNormalizedBlueprintUnderlays,
  withBlueprintUnderlays,
} from './blueprint-underlay-list.helpers';
import { getShapeCenter } from './cutout.helpers';
import { getBuildingById } from './storey.helpers';

/**
 * Rotation and translation a building made: a point at {@link from} lands on
 * {@link to}, and everything turns {@link rotationDeg} clockwise around it.
 */
export type BuildingRigidMove = {
  from: Point2D;
  to: Point2D;
  rotationDeg: number;
};

const RAD_TO_DEG = 180 / Math.PI;

const distance = (a: Point2D, b: Point2D): number =>
  Math.hypot(b[0] - a[0], b[1] - a[1]);

const angleDeg = (from: Point2D, to: Point2D): number =>
  Math.atan2(to[1] - from[1], to[0] - from[0]) * RAD_TO_DEG;

/** Metres a kept distance may drift and still count as the same shape. */
const RIGID_TOLERANCE_METERS = 0.05;

/**
 * True when every point lands where the move puts it, so the shape was moved
 * whole rather than redrawn or mirrored with the same id and point count.
 */
const isRigidlyMoved = (
  points: readonly Point2D[],
  moved: readonly Point2D[],
  { from, to, rotationDeg }: BuildingRigidMove,
): boolean =>
  points.every((point, index) => {
    const movedPoint = moved[index];
    if (!movedPoint) return false;
    const [x, y] = rotatePoint2DClockwise(
      [point[0] - from[0], point[1] - from[1]],
      rotationDeg,
    );
    return (
      distance([x + to[0], y + to[1]], movedPoint) <= RIGID_TOLERANCE_METERS
    );
  });

/**
 * The rigid move between two versions of one building's storeys, read from
 * the first shape kept whole in both. Shapes redrawn under the same id are
 * skipped. Undefined when nothing moved or no shape can be matched.
 */
export const getBuildingRigidMove = (
  previous: readonly Storey[],
  next: readonly Storey[],
): BuildingRigidMove | undefined => {
  const nextShapes = new Map(
    next.flatMap((storey) => storey.shapes).map((shape) => [shape.id, shape]),
  );
  for (const shape of previous.flatMap((storey) => storey.shapes)) {
    const moved = nextShapes.get(shape.id)?.points;
    const from = shape.points[0];
    const to = moved?.[0];
    if (!from || !to || moved.length !== shape.points.length) continue;
    // The point farthest from the first gives the steadiest angle.
    const distances = shape.points.map((point) => distance(from, point));
    const farIndex = distances.indexOf(Math.max(...distances));
    const far = shape.points[farIndex];
    const farMoved = moved[farIndex];
    if (!far || !farMoved || farIndex === 0) continue;
    const rotationDeg = angleDeg(to, farMoved) - angleDeg(from, far);
    if (!isRigidlyMoved(shape.points, moved, { from, to, rotationDeg })) {
      continue;
    }
    const turned = Math.abs(rotationDeg) > 1e-6;
    if (!turned && from[0] === to[0] && from[1] === to[1]) return undefined;
    return { from, to, rotationDeg: turned ? rotationDeg : 0 };
  }
  return undefined;
};

/**
 * Apply {@link move} to the pages of {@link buildingId}. Pages without a
 * building stay where they are, since they belong to no single building.
 */
export const moveBuildingBlueprintUnderlays = (
  underlays: BlueprintUnderlay[],
  buildingId: string,
  move: BuildingRigidMove,
): BlueprintUnderlay[] =>
  underlays.map((underlay) => {
    if (underlay.buildingId !== buildingId) return underlay;
    const offset = rotatePoint2DClockwise(
      [underlay.center[0] - move.from[0], underlay.center[1] - move.from[1]],
      move.rotationDeg,
    );
    return {
      ...underlay,
      center: [offset[0] + move.to[0], offset[1] + move.to[1]],
      rotationDeg: underlay.rotationDeg + move.rotationDeg,
    };
  });

/**
 * Move {@link buildingId}'s pages the way its storeys moved to
 * {@link storeys}. The storeys themselves are left to the caller.
 */
export const moveBuildingBlueprintUnderlaysTo = (
  design: BuildingDesign,
  buildingId: string,
  storeys: readonly Storey[],
): BuildingDesign => {
  const building = getBuildingById(design, buildingId);
  const underlays = getNormalizedBlueprintUnderlays(design);
  if (!building || !underlays.some((page) => page.buildingId === buildingId)) {
    return design;
  }
  const move = getBuildingRigidMove(building.storeys, storeys);
  if (!move) return design;
  return withBlueprintUnderlays(
    design,
    moveBuildingBlueprintUnderlays(underlays, buildingId, move),
  );
};

/**
 * Drop the pages that belong to {@link buildingId} alone.
 */
export const removeBuildingOwnBlueprintUnderlays = (
  design: BuildingDesign,
  buildingId: string,
): BuildingDesign => {
  const underlays = getNormalizedBlueprintUnderlays(design);
  const kept = underlays.filter((page) => page.buildingId !== buildingId);
  return kept.length === underlays.length
    ? design
    : withBlueprintUnderlays(design, kept);
};

/**
 * Middle of {@link buildingId}'s drawn shapes, where a page with nothing to
 * line up with should land. Undefined when the building has no shapes.
 */
export const getBuildingFootprintCenter = (
  design: BuildingDesign,
  buildingId: string | undefined,
): Point2D | undefined => {
  const shapes =
    getBuildingById(design, buildingId)?.storeys.flatMap(
      (storey) => storey.shapes,
    ) ?? [];
  if (!shapes.length) return undefined;
  const centers = shapes.map(getShapeCenter);
  return [
    centers.reduce((sum, [x]) => sum + x, 0) / centers.length,
    centers.reduce((sum, [, y]) => sum + y, 0) / centers.length,
  ];
};
